import { useEffect, useRef, useState, useCallback } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat } from "ol/proj";
import { Feature } from "ol";
import { Point } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Cluster from "ol/source/Cluster";
import Overlay from "ol/Overlay";
import {
  Style,
  Icon,
  Text,
  Fill,
  Stroke,
  Circle as CircleStyle,
} from "ol/style";
import axios from "axios";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import LocationLogo from "../../assets/image/mark-location.png";
import { BASE_URL, HEADERS } from "../../utils/constant";

const TrackAll = ({ currentUserCoords }) => {
  const mapRef = useRef(null);
  const mapObjRef = useRef(null);

  const vectorSourceRef = useRef(new VectorSource());
  const clusterSourceRef = useRef(
    new Cluster({
      distance: 40,
      source: vectorSourceRef.current,
    }),
  );

  const formatAddress = (address) => {
    if (!address) return "";

    const parts = address.split(",");

    return parts.slice(0, 3).join(", ");
  };

  const userLayerRef = useRef(null);
  const popupRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    const map = new Map({
      target: mapRef.current,
      layers: [new TileLayer({ source: new OSM() })],
      view: new View({
        center: fromLonLat([77.1025, 28.7041]),
        zoom: 5,
      }),
    });
    const vectorLayer = new VectorLayer({
      source: clusterSourceRef.current,
      style: (feature) => {
        const size = feature.get("features").length;

        return new Style({
          image:
            size > 1
              ? new CircleStyle({
                  radius: 18,
                  fill: new Fill({ color: "#0d6efd" }),
                  stroke: new Stroke({ color: "#fff", width: 3 }),
                })
              : new Icon({
                  src: LocationLogo,
                  anchor: [0.5, 1],
                  scale: 0.08,
                }),
          text: new Text({
            text: size > 1 ? `${size}` : "",
            fill: new Fill({ color: "#fff" }),
            font: "bold 14px sans-serif",
          }),
        });
      },
    });

    map.addLayer(vectorLayer);
    const popupContainer = document.createElement("div");

    popupContainer.style.background = "#fff";
    popupContainer.style.padding = "10px 12px";
    popupContainer.style.borderRadius = "8px";
    popupContainer.style.boxShadow = "0 4px 14px rgba(0,0,0,0.15)";
    popupContainer.style.minWidth = "200px";
    popupContainer.style.fontSize = "12px";
    popupContainer.style.transition = "all 0.15s ease";

    const overlay = new Overlay({
      element: popupContainer,
      positioning: "bottom-left",
      offset: [10, -10],
    });

    map.addOverlay(overlay);
    popupRef.current = overlay;
    map.on("pointermove", function (evt) {
      const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f);

      if (feature) {
        map.getTargetElement().style.cursor = "pointer";

        const features = feature.get("features");
        const userFeature = features ? features[0] : feature;
        const user = userFeature.get("userData");
        const shortAddress = formatAddress(user.selectedAddress || "");

        if (!user) return;

        popupContainer.innerHTML = `
  <div style="line-height:1.5; max-width:220px;">
    
    <div style="font-weight:600; font-size:13px;">
      ${user.first_name || ""} ${user.last_name || ""}
    </div>

    <div style="color:#6c757d; font-size:11px; margin-bottom:6px;">
      ${user.role}
    </div>

    <div style="margin-bottom:4px;">
      📞 ${user.phone_number || "-"}
    </div>

    <div style="margin-bottom:4px;">
      <span style="color:${
        user.gps_status ? "#28a745" : "#dc3545"
      }; font-size:11px;">
        ● ${user.gps_status ? "GPS Active" : "GPS Off"}
      </span>
    </div>

    <div style="margin-bottom:4px; font-size:11px;">
      ⏱ ${user.today_attendance?.status || "-"}
    </div>

    <div style="
      margin-top:6px;
      font-size:11px;
      color:#0d6efd;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
    " title="${user.selectedAddress || ""}">
      ${shortAddress}
    </div>

  </div>
`;

        overlay.setPosition(evt.coordinate);
      } else {
        map.getTargetElement().style.cursor = "";
        overlay.setPosition(undefined);
      }
    });
    map.on("click", function (evt) {
      const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f);

      if (feature) {
        const features = feature.get("features");

        if (features && features.length > 1) {
          const view = map.getView();
          view.animate({
            zoom: view.getZoom() + 2,
            center: evt.coordinate,
            duration: 300,
          });
        }
      }
    });

    mapObjRef.current = map;

    return () => map.setTarget(null);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/users/tms_users/?role=technician&role=sales&search=${searchTerm}&page_size=all`,
        { headers: HEADERS },
      );

      updateMarkers(res.data.results);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
    const interval = setInterval(fetchUsers, 60000);
    return () => clearInterval(interval);
  }, [fetchUsers]);

  const updateMarkers = (users) => {
    vectorSourceRef.current.clear();

    const features = users
      .filter((u) => u.latitude && u.longitude && u.gps_status)
      .map((u) => {
        const feature = new Feature({
          geometry: new Point(
            fromLonLat([parseFloat(u.longitude), parseFloat(u.latitude)]),
          ),
        });

        feature.set("userData", u);

        return feature;
      });

    vectorSourceRef.current.addFeatures(features);
  };

  useEffect(() => {
    if (!currentUserCoords || !mapObjRef.current) return;

    if (userLayerRef.current) {
      mapObjRef.current.removeLayer(userLayerRef.current);
    }

    const feature = new Feature({
      geometry: new Point(
        fromLonLat([
          parseFloat(currentUserCoords.longitude),
          parseFloat(currentUserCoords.latitude),
        ]),
      ),
    });

    const layer = new VectorLayer({
      source: new VectorSource({ features: [feature] }),
      style: new Style({
        image: new CircleStyle({
          radius: 10,
          fill: new Fill({ color: "green" }),
          stroke: new Stroke({ color: "#fff", width: 3 }),
        }),
      }),
    });

    mapObjRef.current.addLayer(layer);
    userLayerRef.current = layer;
  }, [currentUserCoords]);

  return (
    <Container fluid className="p-3">
      <Row className="mb-3">
        <Col md={6}>
          <h4>📍 Live Location Tracking</h4>
        </Col>

        <Col md={3}>
          <Form.Control
            placeholder="Search technician..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Button onClick={fetchUsers}>Refresh</Button>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <div
            ref={mapRef}
            style={{
              width: "100%",
              height: "80vh",
              borderRadius: "12px",
              border: "1px solid #ddd",
            }}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default TrackAll;
